import { useEffect, useState } from 'react';
import { spore } from '@/spore/runtime';
import { Switch } from '@/components/ui/switch';
import { Panel } from './Panel';

function useKeysDown() {
  const [down, setDown] = useState({});
  useEffect(() => {
    const set = (v) => (e) => setDown((d) => ({ ...d, [e.key.toLowerCase()]: v }));
    const dn = set(true);
    const up = set(false);
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', dn);
      window.removeEventListener('keyup', up);
    };
  }, []);
  return down;
}

export const SimulatorPanel = ({ sim }) => {
  const keys = useKeysDown();
  const strokeOn = (on) => () => spore.sim.setStroke(on);
  return (
    <Panel
      testId="simulator-panel"
      code="05 / SIMULATOR"
      title="Specimen Simulator"
      delay={260}
      right={
        <label className="flex items-center gap-2 text-xs tracking-[0.2em]">
          <span data-testid="simulator-status" className={sim.enabled ? 'glow-amber' : 'text-[var(--dim)]'}>{sim.enabled ? 'ON' : 'OFF'}</span>
          <Switch data-testid="simulator-toggle" checked={sim.enabled} onCheckedChange={(v) => spore.sim.setEnabled(v)} />
        </label>
      }
    >
      <p className="mb-4 text-sm text-[var(--muted)]">Injects synthetic events into the same pipeline as the Biotron.</p>
      <div className="mb-5 space-y-2 text-sm">
        <div className="flex items-center gap-3"><span className={`kbd ${keys.s ? 'down' : ''}`}>S</span> toggle simulator</div>
        <div className="flex items-center gap-3"><span className={`kbd ${(keys.q || keys.w) && sim.enabled ? 'down' : ''}`}>W / Q</span> hold = touching (short = tap, long = stroke)</div>
        <div className="flex items-center gap-3"><span className="kbd text-[var(--dim)]">—</span> release = not touching</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button
          data-testid="simulator-stroke-button"
          className={`btn-lab ${sim.stroking ? 'active' : ''}`}
          disabled={!sim.enabled}
          onPointerDown={strokeOn(true)}
          onPointerUp={strokeOn(false)}
          onPointerLeave={strokeOn(false)}
        >
          Touch (hold)
        </button>
        <button data-testid="simulator-hit-button" className="btn-lab" disabled={!sim.enabled} onClick={() => spore.sim.hit()}>
          Tap
        </button>
      </div>
      <div data-testid="simulator-hit-count" className="mt-3 text-xs text-[var(--muted)]">SIM HITS SENT · {sim.hits}</div>
      <div className="mt-4 border-t border-white/10 pt-3">
        <div className="label-eyebrow mb-2">GLITCH SIMULATOR · faulty-word safeguard</div>
        {[['phantomTap', 'Inject phantom TAP spam'], ['stuckTouch', 'Stuck touch'], ['stuckHover', 'Stuck hover']].map(([k, label]) => (
          <label key={k} className="flex items-center justify-between py-1 text-sm">
            <span>{label}</span>
            <Switch data-testid={`glitch-${k}`} checked={!!sim[k]} onCheckedChange={(v) => spore.sim.setGlitch(k, v)} />
          </label>
        ))}
      </div>
    </Panel>
  );
};
